import React, { useState } from 'react';

// Improved CSV parser to handle quoted values and preserve full phone numbers
function parseCSV(csvText) {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const phoneIdx = headers.findIndex(h => h === 'phone');
  if (phoneIdx === -1) return [];
  return lines.slice(1).map(line => {
    // Use a regex to split CSV line into columns, handling quoted values with commas
    const cols = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++; // skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        cols.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    cols.push(current);

    // Remove leading/trailing whitespace and possible surrounding quotes
    const clean = (val) => {
      let v = val.trim();
      if (v.startsWith('"') && v.endsWith('"')) {
        v = v.slice(1, -1).replace(/""/g, '"');
      }
      return v;
    };

    const raw = clean(cols[phoneIdx] || '');
    const digitsOnly = raw.replace(/\D/g, ''); // Remove all non-digits, but keep all digits
    return { Number: digitsOnly };
  }).filter(row => row.Number);
}

// Helper function to wait for a given ms
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function PhoneTypeChecker() {
  const [apiKey, setApiKey] = useState('ee0dab2289bfb7251858680e759f63f5');
  const [csvNumbers, setCsvNumbers] = useState([]); // [{ Number }]
  const [results, setResults] = useState([]); // [{ Number, LineType, Status }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [csvFileName, setCsvFileName] = useState('');

  // Handle CSV file upload and parse
  const handleFileUpload = (e) => {
    setError('');
    setResults([]);
    const file = e.target.files[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        setError('CSV must have a header "phone" and at least one row.');
        setCsvNumbers([]);
      } else {
        setCsvNumbers(parsed);
      }
    };
    reader.onerror = () => setError('Failed to read file.');
    reader.readAsText(file);
  };

  // Fetch line type for all numbers in csvNumbers, with a delay between each API call
  const fetchAllLineTypes = async () => {
    setError('');
    setResults([]);
    if (!apiKey) {
      setError('Please enter API key.');
      return;
    }
    if (!csvNumbers.length) {
      setError('Please upload a CSV file with phone numbers.');
      return;
    }
    setLoading(true);

    // We'll use a local variable to keep the results in order
    let newResults = [];

    for (let i = 0; i < csvNumbers.length; i++) {
      const number = csvNumbers[i].Number;
      if (!number) {
        newResults.push({ Number: '', LineType: '', Status: 'Missing number' });
        setResults([...newResults]);
        // eslint-disable-next-line no-await-in-loop
        await sleep(10000);
        continue;
      }

      // eslint-disable-next-line no-loop-func
      (async (idx, num) => {
        const url = `https://apilayer.net/api/validate?access_key=${encodeURIComponent(apiKey)}&number=${encodeURIComponent(num)}`;
        try {
          const response = await fetch(url);
          if (!response.ok) {
            newResults[idx] = { Number: num, LineType: '', Status: `HTTP error: ${response.status}` };
            setResults([...newResults]);
            return;
          }
          const data = await response.json();
          newResults[idx] = {
            Number: data.number || num,
            LineType: data.line_type || 'N/A',
            Status: data.success === false ? (data.error?.info || 'API error') : 'OK'
          };
        } catch (err) {
          newResults[idx] = { Number: num, LineType: '', Status: err.message || 'Fetch error' };
        }
        setResults([...newResults]);
      })(i, number);

      // Add a delay between API calls to avoid rate limiting (e.g., 1000ms)
      // eslint-disable-next-line no-await-in-loop
      await sleep(2000);
    }

    // Wait for all requests to finish before setting loading to false
    // Since the last request is async, we need to wait for all to finish
    // We'll poll until all results are filled
    const pollUntilDone = () => {
      return new Promise(resolve => {
        const check = () => {
          if (newResults.length === csvNumbers.length && newResults.every(r => r && r.Number !== undefined)) {
            resolve();
          } else {
            setTimeout(check, 200);
          }
        };
        check();
      });
    };
    await pollUntilDone();
    setLoading(false);
  };

  return (
    <div style={{ padding: 24, maxWidth: 600, margin: '0 auto', fontFamily: 'Segoe UI, Arial, sans-serif' }}>
      <h2>Phone Number Line Type Checker</h2>
      <div style={{ marginBottom: 16 }}>
        <label>
          <div style={{ marginBottom: 4 }}>API Key:</div>
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Enter API key"
            style={{ width: '100%', padding: 8, marginBottom: 8 }}
          />
        </label>
        <label>
          <div style={{ marginBottom: 4 }}>Upload CSV File (header: phone):</div>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ width: '100%', padding: 8 }}
          />
        </label>
        {csvFileName && (
          <div style={{ marginTop: 6, color: '#555', fontSize: 13 }}>
            Loaded file: <strong>{csvFileName}</strong> ({csvNumbers.length} numbers)
          </div>
        )}
      </div>
      <button
        onClick={fetchAllLineTypes}
        disabled={loading || !csvNumbers.length}
        style={{
          padding: '10px 16px',
          background: '#6A1B9A',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          cursor: loading || !csvNumbers.length ? 'not-allowed' : 'pointer'
        }}
      >
        {loading ? 'Checking...' : 'Check Line Types'}
      </button>
      {error && <div style={{ color: 'red', marginTop: 12, marginBottom: 8 }}>{error}</div>}
      {results.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3>Results</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 400 }}>
              <thead>
                <tr style={{ background: '#f3e5f5' }}>
                  <th style={{ border: '1px solid #ccc', padding: 8 }}>Number</th>
                  <th style={{ border: '1px solid #ccc', padding: 8 }}>Line Type</th>
                  {/* <th style={{ border: '1px solid #ccc', padding: 8 }}>Status</th> */}
                </tr>
              </thead>
              <tbody>
                {results.map((row, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                    <td style={{ border: '1px solid #ccc', padding: 8 }}>{row.Number}</td>
                    <td style={{ border: '1px solid #ccc', padding: 8 }}>{row.LineType}</td>
                    {/* <td style={{ border: '1px solid #ccc', padding: 8 }}>{row.Status}</td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default PhoneTypeChecker;
